import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useGetBontonInvoiceListMutation, useGetBontonListingItemDetailsMutation } from "../../../features/supplyBeam/supplyBeamData/bontonListingAPI";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import ListingItemDetailsModal from "./ListingItemDetailsModal";
import { exportToCSV } from "../../../utils/exportCsv";

const InvoiceList = () => {
    const [
        fetchInvoiceList,
        {
            data: invoiceListData,
            error: invoiceListError,
            isLoading: invoiceListIsLoading,
        },
    ] = useGetBontonInvoiceListMutation();
    const [
        fetchItemDetails,
        {
            data: itemDetailsData,
            error: itemDetailsError,
            isLoading: itemDetailsIsLoading,
        },
    ] = useGetBontonListingItemDetailsMutation();

    const [myList, setMyList] = useState([]);
    const [showItemDetailsModal, setShowItemDetailsModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);

    const initialRequest = {
        page: 1,
        limit: 10,
        invoice_no: "",
        order_no: "",
        status: "",
        start_date: "",
        end_date: "",
    };

    const [request, setRequest] = useState(initialRequest);

    const clearFilters = () => {
        setRequest(initialRequest);
        setCurrentPage(1);
        setOffset(0);
    };

    useEffect(() => {
        fetchInvoiceListFunction();
    }, [request]);

    const fetchInvoiceListFunction = () => {
        fetchInvoiceList({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            data: request,
        });
    };

    useEffect(() => {
        if (invoiceListData) {
            setMyList(invoiceListData?.data?.list || []);
            setTotalRows(invoiceListData?.data?.pagination?.total || 0);
        }
        if (invoiceListError) {
            toast.dismiss();
            toast.error("Something went wrong", { id: "invoiceListError" });
        }
    }, [invoiceListData, invoiceListError]);

    useEffect(() => {
        if (itemDetailsError) {
            toast.dismiss();
            toast.error("Unable to fetch item details", { id: "itemDetailsError" });
            setShowItemDetailsModal(false);
        }
    }, [itemDetailsError]);

    const handleViewDetails = (row) => {
        setSelectedRow(row);
        setShowItemDetailsModal(true);
        fetchItemDetails({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            data: {
                type: "invoice",
                id: row.id,
                invoice_no: row.invoice_no,
                order_no: row.order_no,
            },
        });
    };

    const handlePageChange = (pageNumber) => {
        const newOffset = (pageNumber - 1) * limit;
        setOffset(newOffset);
        setRequest((prev) => ({ ...prev, page: pageNumber }));
        setCurrentPage(pageNumber);
    };

    const handlePerRowsChange = (newPerPage) => {
        setRequest((prev) => ({
            ...prev,
            limit: newPerPage,
            page: 1,
        }));
        setLimit(newPerPage);
        setOffset(0);
        setCurrentPage(1);
        setPaginationResetToggle((prev) => !prev);
    };

    const columns = [
        {
            name: "SNo",
            cell: (row, idx) => offset + Number(idx + 1),
            sortable: true,
            width: "80px",
        },
        {
            name: "Invoice No",
            cell: (row) => row.invoice_no,
            sortable: true,
            width: "200px",
        },
        {
            name: "Order No",
            cell: (row) => row.order_no,
            sortable: true,
            width: "200px",
        },
        {
            name: "Invoice Date",
            cell: (row) => row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : "-",
            sortable: true,
            width: "150px",
        },
        {
            name: "Dispatch Type",
            cell: (row) => row.dispatch_type || "-",
            sortable: true,
            width: "150px",
        },
        {
            name: "Out Code",
            cell: (row) => row.out_code || "-",
            sortable: true,
            width: "150px",
        },
        {
            name: "Status",
            cell: (row) => {
                const s = row.status;
                if (s === "1") {
                    return (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            Active
                        </span>
                    );
                }
                if (s === "0") {
                    return (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                            Inactive
                        </span>
                    );
                }
                return (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                        {s || "-"}
                    </span>
                );
            },
            sortable: true,
            width: "120px",
        },
        {
            name: "Remarks",
            cell: (row) => row.remarks || "-",
            sortable: true,
            width: "200px",
        },
        {
            name: "Created At",
            cell: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : "-",
            sortable: true,
            width: "200px",
        },
        {
            name: "Action",
            cell: (row) => (
                <button
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                    onClick={() => handleViewDetails(row)}
                >
                    View
                </button>
            ),
            width: "110px",
        },
    ];

    const handleExportCSV = () => {
        if (!myList || myList.length === 0) {
            toast.error("No data available to export");
            return;
        }
        const exportColumns = [
            { name: "Invoice No", selector: (r) => r.invoice_no || "-" },
            { name: "Order No", selector: (r) => r.order_no || "-" },
            { name: "Total SKU", selector: (r) => r.total_sku ?? "-" },
            { name: "Qty", selector: (r) => r.qty ?? "-" },
            { name: "Total Amount", selector: (r) => r.total_amount ?? "-" },
            { name: "Status", selector: (r) => r.status === "1" ? "Active" : r.status === "0" ? "Inactive" : r.status || "-" },
            { name: "Created At", selector: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "-" },
        ];
        exportToCSV(myList, exportColumns, "bonton_invoice_list.csv");
        toast.success("CSV Downloaded Successfully!");
    };

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold flex justify-between items-center pr-6">
                        <div>
                            Invoice List
                            <LinksComponent />
                        </div>
                        <button
                            className="rounded-md bg-green-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-green-700 transition shadow-sm"
                            onClick={handleExportCSV}
                        >
                            Export CSV
                        </button>
                    </div>

                    <div className="p-3">
                        <div className="w-full bg-white shadow rounded-lg">
                            <div>
                                <DataTableComponent
                                    keyField={(row) => row.id}
                                    columns={columns}
                                    data={myList}
                                    loading={invoiceListIsLoading}
                                    totalRows={totalRows}
                                    handlePerRowsChange={handlePerRowsChange}
                                    handlePageChange={handlePageChange}
                                    paginationDefaultPage={currentPage}
                                    paginationResetDefaultPage={paginationResetToggle}
                                >
                                    <div className="bg-gray-50 border-b border-gray-200 p-4">
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">Invoice No</label>
                                                <input
                                                    type="text"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    placeholder="Search Invoice No..."
                                                    value={request.invoice_no}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, invoice_no: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">Order No</label>
                                                <input
                                                    type="text"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    placeholder="Search Order No..."
                                                    value={request.order_no}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, order_no: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">Status</label>
                                                <select
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                                                    value={request.status}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, status: e.target.value, page: 1 })
                                                    }
                                                >
                                                    <option value="">All</option>
                                                    <option value="1">Active</option>
                                                    <option value="0">Inactive</option>
                                                </select>
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">From Date</label>
                                                <input
                                                    type="date"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    value={request.start_date}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, start_date: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">To Date</label>
                                                <input
                                                    type="date"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    value={request.end_date}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, end_date: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex items-end gap-2">
                                                <button
                                                    className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 transition shadow-sm"
                                                    onClick={fetchInvoiceListFunction}
                                                >
                                                    Search
                                                </button>
                                                <button
                                                    className="rounded-md bg-gray-200 px-4 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-300 transition"
                                                    onClick={clearFilters}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </DataTableComponent>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showItemDetailsModal && (
                <ListingItemDetailsModal
                    show={showItemDetailsModal}
                    setShow={setShowItemDetailsModal}
                    title={`Invoice Items - ${selectedRow?.invoice_no || ""}`}
                    data={itemDetailsData}
                    loading={itemDetailsIsLoading}
                />
            )}
        </>
    );
};

export default InvoiceList;
